import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useRef } from 'react'

import { useFrame } from './engine'
import { lerp, prog, seg, smooth } from './motion'

export const COLORS = {
  blue: '#407BFF',
  green: '#43DBBD',
  yellow: '#FFB628',
  pink: '#FE79A7',
  ink: '#182033',
  grey: '#849BB3',
  line: '#E3E8F0',
  pale: '#EBEFFD',
  bg: '#F6F7F9'
}

/** Absolut positionierter Block in Bühnenkoordinaten (960 × 640). */
export function Box({ x, y, w, h, className, style, children, ...rest }) {
  return (
    <div
      className={clsx('sty-box', className)}
      style={{ left: x, top: y, width: w, height: h, ...style }}
      {...rest}
    >
      {children}
    </div>
  )
}

Box.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  w: PropTypes.number,
  h: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
}

/** Aivy-Grundformen: Dreieck, Quadrat, Kreis. Flach, ohne Verlauf. */
export function Shape({
  kind,
  color,
  size = 24,
  rotate = 0,
  className,
  style
}) {
  const fill = COLORS[color] || color
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      className={className}
      style={{
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        ...style
      }}
      aria-hidden='true'
    >
      {kind === 'tri' && <path d='M12 2 L23 21 H1 Z' fill={fill} />}
      {kind === 'sq' && (
        <rect x='2' y='2' width='20' height='20' rx='2' fill={fill} />
      )}
      {kind === 'dot' && <circle cx='12' cy='12' r='11' fill={fill} />}
    </svg>
  )
}

Shape.propTypes = {
  kind: PropTypes.oneOf(['tri', 'sq', 'dot']).isRequired,
  color: PropTypes.string.isRequired,
  size: PropTypes.number,
  rotate: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object
}

export function Avatar({ initials, color = 'blue', size = 40, className }) {
  return (
    <div
      className={clsx('sty-avatar', className)}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: COLORS[color] || color
      }}
    >
      {initials}
    </div>
  )
}

Avatar.propTypes = {
  initials: PropTypes.string.isRequired,
  color: PropTypes.string,
  size: PropTypes.number,
  className: PropTypes.string
}

/** Smartphone-Rahmen, Inhalt liegt absolut im Screen (240 × 520). */
export function Phone({ x, y, style, children }) {
  return (
    <Box x={x} y={y} w={260} h={540} className='sty-phone' style={style}>
      <div className='sty-phone__screen'>
        <div className='sty-phone__notch' />
        {children}
      </div>
    </Box>
  )
}

Phone.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  style: PropTypes.object,
  children: PropTypes.node
}

export function Check({ color = 'green', size = 20 }) {
  return (
    <span
      className='sty-check'
      style={{ width: size, height: size, background: COLORS[color] || color }}
      aria-hidden='true'
    >
      <svg viewBox='0 0 20 20' width={size * 0.6} height={size * 0.6}>
        <path
          d='M4 10.5 8 14.5 16 6'
          fill='none'
          stroke='#fff'
          strokeWidth='2.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </span>
  )
}

Check.propTypes = { color: PropTypes.string, size: PropTypes.number }

/**
 * Ideal- und Übergangsbereich wie im BeamGraph der PDF-Reports, als Füllung
 * einer `.sty-track`. Mit `at` ziehen beide Bereiche beim Scrollen auf.
 */
export function BeamBands({ ideal, trans, at }) {
  const grow = at ? { ...prog(at), transform: 'scaleX(var(--p))' } : null
  return (
    <>
      <i
        className='sty-beam-trans'
        style={{
          left: `${trans[0]}%`,
          width: `${trans[1] - trans[0]}%`,
          ...grow
        }}
      />
      <i
        className='sty-beam-ideal'
        style={{
          left: `${ideal[0]}%`,
          width: `${ideal[1] - ideal[0]}%`,
          ...grow
        }}
      />
    </>
  )
}

BeamBands.propTypes = {
  ideal: PropTypes.arrayOf(PropTypes.number).isRequired,
  trans: PropTypes.arrayOf(PropTypes.number).isRequired,
  at: PropTypes.arrayOf(PropTypes.number)
}

const FIT_PATHS = {
  high: 'M4 10.5 8 14.5 16 6',
  mid: 'M5 10 H15',
  low: 'M10 4.5 V15 M5.5 10.5 10 15 14.5 10.5'
}

/** Stufe einer Dimension wie im Passungsprofil: hoch, mittel oder niedrig. */
export function FitIcon({ fit, size = 20 }) {
  return (
    <span
      className={clsx('sty-fit', `sty-fit--${fit}`)}
      style={{ width: size, height: size }}
      aria-hidden='true'
    >
      <svg viewBox='0 0 20 20' width={size * 0.6} height={size * 0.6}>
        <path
          d={FIT_PATHS[fit]}
          fill='none'
          stroke='#fff'
          strokeWidth='2.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </span>
  )
}

FitIcon.propTypes = {
  fit: PropTypes.oneOf(['high', 'mid', 'low']).isRequired,
  size: PropTypes.number
}

/** Zählt im Bereich `at` von `from` nach `to`. */
export function Count({ to, from = 0, at, suffix = '', className }) {
  const ref = useRef(null)
  useFrame((l) => {
    if (!ref.current) return
    const v = Math.round(lerp(from, to, smooth(seg(l, at[0], at[1]))))
    ref.current.textContent = `${v}${suffix}`
  })
  return (
    <span ref={ref} className={className}>
      {from}
      {suffix}
    </span>
  )
}

Count.propTypes = {
  to: PropTypes.number.isRequired,
  from: PropTypes.number,
  at: PropTypes.arrayOf(PropTypes.number).isRequired,
  suffix: PropTypes.string,
  className: PropTypes.string
}

/**
 * Tippt `text` im Bereich `at`. Der Rest steht unsichtbar daneben, damit die
 * Blase von Anfang an ihre endgültige Höhe hat.
 */
export function Typed({ text, at, className }) {
  const shown = useRef(null)
  const rest = useRef(null)
  useFrame((l) => {
    if (!shown.current || !rest.current) return
    const count = Math.round(text.length * seg(l, at[0], at[1]))
    shown.current.textContent = text.slice(0, count)
    rest.current.textContent = text.slice(count)
  })
  return (
    <span className={className}>
      <span ref={shown} />
      <span ref={rest} className='sty-typed-rest'>
        {text}
      </span>
    </span>
  )
}

Typed.propTypes = {
  text: PropTypes.string.isRequired,
  at: PropTypes.arrayOf(PropTypes.number).isRequired,
  className: PropTypes.string
}

/** Fortschrittsring, füllt sich im Bereich `at` bis `value` Prozent. */
export function Ring({
  value,
  at,
  size = 160,
  stroke = 14,
  color = 'blue',
  children
}) {
  const ref = useRef(null)
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  useFrame((l) => {
    if (!ref.current) return
    const p = smooth(seg(l, at[0], at[1])) * (value / 100)
    ref.current.style.strokeDashoffset = String(c * (1 - p))
  })
  return (
    <div className='sty-ring' style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden='true'
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill='none'
          stroke={COLORS.pale}
          strokeWidth={stroke}
        />
        <circle
          ref={ref}
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill='none'
          stroke={COLORS[color] || color}
          strokeWidth={stroke}
          strokeLinecap='round'
          strokeDasharray={c}
          strokeDashoffset={c}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className='sty-ring__label'>{children}</div>
    </div>
  )
}

Ring.propTypes = {
  value: PropTypes.number.isRequired,
  at: PropTypes.arrayOf(PropTypes.number).isRequired,
  size: PropTypes.number,
  stroke: PropTypes.number,
  color: PropTypes.string,
  children: PropTypes.node
}
